import { ErrorHandler, Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { LoggerService } from './logger.service';

interface AppError {
  message: string;
  code?: string;
  details?: unknown;
  stack?: string;
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly logger = inject(LoggerService);

  handleError(error: Error | HttpErrorResponse | unknown): void {
    if (error instanceof HttpErrorResponse) {
      // Server/Network error
      this.handleServerError(error);
    } else if (error instanceof Error) {
      // Client-side error
      this.handleClientError(error);
    } else {
      // Unknown error type
      this.handleUnknownError(error);
    }
  }

  private handleServerError(error: HttpErrorResponse): void {
    const appError: AppError = {
      message: error.message || 'Server error occurred',
      code: error.status?.toString(),
      details: {
        status: error.status,
        statusText: error.statusText,
        url: error.url,
        error: error.error,
      },
    };

    this.logger.error('Server Error', appError);

    // Handle specific HTTP status codes
    switch (error.status) {
      case 0:
        this.logger.warn('Network error - check internet connection');
        break;
      case 401:
        this.logger.warn('Unauthorized - user may need to re-authenticate');
        break;
      case 403:
        this.logger.warn('Forbidden - insufficient permissions');
        break;
      case 404:
        this.logger.warn('Resource not found');
        break;
      case 500:
        this.logger.error('Internal server error');
        break;
      default:
        this.logger.error(`HTTP ${error.status}: ${error.statusText}`);
    }
  }

  private handleClientError(error: Error): void {
    // Suppress Firebase Performance attribute value errors (non-critical)
    if (error.message?.includes('Performance: Attribute value') && error.message?.includes('is invalid')) {
      // These errors occur when Firebase Performance tries to track DOM elements with very long class names
      // They don't affect functionality and can be safely ignored
      return;
    }

    // Handle Firebase permission errors with more context
    if (error.message?.includes('Missing or insufficient permissions') || (error.name === 'FirebaseError' && (error as any).code === 'permission-denied')) {
      const firebaseError = error as { code?: string; details?: unknown; path?: string; stack?: string };
      
      // Try to extract collection/document path from stack trace or error details
      const context = this.extractFirebaseContext(error, firebaseError);
      
      // Suppress non-critical Firebase permission errors for public collections
      // These occur because we've migrated to JWT auth and no longer use Firebase Auth
      // The app now uses API services instead of Firestore for these collections
      const publicCollections = ['categories', 'vendors', 'promotions', 'coupons'];
      if (context.collection && publicCollections.includes(context.collection.toLowerCase())) {
        // Silently suppress these errors - they're non-critical since we use API services
        this.logger.debug(`Suppressed Firebase permission error for public collection: ${context.collection}`, {
          collection: context.collection,
          note: 'App uses API services instead of Firestore for this collection',
        });
        return; // Don't log or show to user
      }
      
      // Determine operation type from stack trace
      const operation = this.extractOperationType(error.stack || '');
      
      // Build specific error message
      let specificMessage = 'Firebase Permission Error';
      if (context.collection) {
        specificMessage += ` accessing ${context.collection}`;
        if (context.documentId) {
          specificMessage += `/${context.documentId}`;
        }
      }
      if (operation) {
        specificMessage += ` (${operation})`;
      }
      
      // Build suggestion based on context
      let suggestion = 'Check Firestore security rules.';
      if (context.collection) {
        suggestion += ` Ensure rules allow ${operation || 'access'} for ${context.collection} collection.`;
        if (context.documentId) {
          suggestion += ` Document ID: ${context.documentId}`;
        }
      } else {
        suggestion += ' Ensure rules allow public reads for categories, vendors, and promotions collections.';
      }
      
      this.logger.error(specificMessage, {
        message: error.message,
        code: firebaseError.code || 'permission-denied',
        name: error.name,
        collection: context.collection,
        documentId: context.documentId,
        operation: operation,
        path: context.path,
        stack: error.stack,
        details: firebaseError.details,
        suggestion: suggestion,
      });
      return; // Don't log again as generic "Client Error"
    }

    const appError: AppError = {
      message: error.message,
      stack: error.stack,
      details: {
        name: error.name,
      },
    };

    this.logger.error('Client Error', appError);

    // Handle specific error types
    if (error.name === 'ChunkLoadError') {
      this.logger.warn('Chunk load error - may need to refresh page');
    } else if (error.message.includes('ResizeObserver')) {
      // Ignore ResizeObserver errors (common, non-critical)
      this.logger.debug('ResizeObserver error (non-critical)', error);
    }
  }

  private handleUnknownError(error: unknown): void {
    const appError: AppError = {
      message: 'Unknown error occurred',
      details: error,
    };

    this.logger.error('Unknown Error', appError);
  }

  /**
   * Extract Firebase context (collection, document, path) from error
   */
  private extractFirebaseContext(error: Error, firebaseError: { path?: string; details?: unknown }): {
    collection?: string;
    documentId?: string;
    path?: string;
  } {
    const context: { collection?: string; documentId?: string; path?: string } = {};
    
    // Try to get path from error details
    if (firebaseError.path) {
      context.path = firebaseError.path;
      const pathParts = firebaseError.path.split('/');
      if (pathParts.length >= 2) {
        context.collection = pathParts[1];
        if (pathParts.length >= 3) {
          context.documentId = pathParts[2];
        }
      }
    }
    
    // Try to extract from stack trace
    if (!context.collection && error.stack) {
      // Look for collection names in stack trace
      const collectionMatch = error.stack.match(/(?:COLLECTIONS\.|collection\([^,]+,\s*['"])(\w+)/);
      if (collectionMatch) {
        context.collection = collectionMatch[1];
      }
      
      // Look for document paths in stack trace
      const pathMatch = error.stack.match(/(?:doc\([^,]+,\s*['"])([^'"]+)/);
      if (pathMatch) {
        const fullPath = pathMatch[1];
        const pathParts = fullPath.split('/');
        if (pathParts.length >= 1) {
          context.collection = pathParts[0];
          if (pathParts.length >= 2) {
            context.documentId = pathParts[1];
          }
          context.path = fullPath;
        }
      }
    }
    
    // Try to extract from error details if it's an object
    if (!context.collection && firebaseError.details && typeof firebaseError.details === 'object') {
      const details = firebaseError.details as Record<string, unknown>;
      if (details['path']) {
        context.path = String(details['path']);
        const pathParts = context.path.split('/');
        if (pathParts.length >= 2) {
          context.collection = pathParts[1];
          if (pathParts.length >= 3) {
            context.documentId = pathParts[2];
          }
        }
      }
    }
    
    return context;
  }

  /**
   * Extract operation type (read, write, create, update, delete) from stack trace
   */
  private extractOperationType(stack: string): string | undefined {
    if (!stack) return undefined;
    
    // Common Firestore operation patterns
    const operations = [
      { pattern: /(?:getDoc|docData|collectionData|listCategories|listVendors|listPromotions|getVendor|getOrder)/i, name: 'read' },
      { pattern: /(?:setDoc|createCategory|createVendor|createPromotion|addDoc)/i, name: 'create' },
      { pattern: /(?:updateDoc|updateCategory|updateVendor|updateOrder|updatePromotion)/i, name: 'update' },
      { pattern: /(?:deleteDoc|deleteCategory|deleteVendor|deletePromotion)/i, name: 'delete' },
      { pattern: /(?:runTransaction)/i, name: 'transaction' },
    ];
    
    for (const op of operations) {
      if (op.pattern.test(stack)) {
        return op.name;
      }
    }
    
    return undefined;
  }
}


